import React, { FormEvent } from "react";
import { AlertTriangle, CheckCircle, Upload, FileText, Image } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  ReviewClaimConfirmationFormData,
  ReviewClaimConfirmationFormDataKey,
  Claim,
  ReviewClaimConfirmationFormAction
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { TextInputBox, TextInputBoxLarge, MultiFileUpload } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { StatusPill } from "src/components/ui";
import { DD_MM_YYYY } from "src/helpers/dateHelper";
import { Link } from "react-router-dom";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { ExternalLink } from "lucide-react";

interface ReviewClaimConfirmationFormProps {
  formData: ReviewClaimConfirmationFormData;
  claimToReview?: Claim;
  imageUrls?: string[];
  isLoadingImages?: boolean;
  validationErrors?: ValidationErrors<ReviewClaimConfirmationFormDataKey>;
  formLvlErrorMessage: string;
  action: ReviewClaimConfirmationFormAction;
  handleInputChange: (
    field: ReviewClaimConfirmationFormDataKey,
    value: ReviewClaimConfirmationFormData[keyof ReviewClaimConfirmationFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: ReviewClaimConfirmationFormAction) => void;
}

const ReviewClaimConfirmationForm: React.FC<ReviewClaimConfirmationFormProps> = ({
  formData,
  claimToReview,
  imageUrls = [],
  isLoadingImages = false,
  validationErrors,
  formLvlErrorMessage,
  action,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Review Claim Confirmation</h1>
            <p className="text-gray-300 mt-2 text-lg">Review claim details and confirm with credit file</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              {!claimToReview && (
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Claim Search
                  </h3>
                  <TextInputBox
                    id="claim-id"
                    placeholder="Claim ID"
                    onChange={(value) => handleInputChange("claimId", value.toUpperCase())}
                    value={formData.claimId}
                  />
                  <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("claimId")} />
                </div>
              )}

              {claimToReview && (
                <>
                  {/* Claim Header */}
                  <section className="overflow-hidden rounded-2xl border border-secondary-200 shadow-soft bg-white">
                    <div className="bg-gradient-to-r from-secondary-900 via-secondary-800 to-secondary-700 px-8 py-6 sm:px-10 sm:py-8">
                      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-4">
                          <div className="rounded-xl bg-white/10 p-3 text-white">
                            <AlertTriangle className="h-7 w-7" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-200">
                              Claim
                            </p>
                            <h2 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl font-mono">
                              {claimToReview.id}
                            </h2>
                            <p className="mt-3 text-sm text-secondary-100">Review claim details before confirmation.</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-end">
                          <StatusPill status={claimToReview.status} />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Basic Information Section */}
                  <section className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
                      <AlertTriangle className="h-5 w-5 text-primary-600" />
                      <h3 className="text-base font-semibold text-secondary-900">Basic Information</h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-secondary-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                              Purchase Order ID
                            </span>
                          </div>
                          {claimToReview.purchaseOrderId ? (
                            <div className="flex items-center gap-2">
                              <Link
                                to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${claimToReview.purchaseOrderId}`}
                                className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors font-mono flex items-center gap-1"
                              >
                                {claimToReview.purchaseOrderId}
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            </div>
                          ) : (
                            <p className="text-sm font-medium text-secondary-900">-</p>
                          )}
                        </div>
                        <div className="bg-secondary-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                              Vendor Name
                            </span>
                          </div>
                          <p className="text-sm font-medium text-secondary-900">{claimToReview.vendorName}</p>
                        </div>
                        <div className="bg-secondary-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                              Issue Type
                            </span>
                          </div>
                          <p className="text-sm font-medium text-secondary-900 capitalize">
                            {claimToReview.issueType || "-"}
                          </p>
                        </div>
                        <div className="bg-secondary-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                              Claim Price
                            </span>
                          </div>
                          <p className="text-lg font-semibold text-secondary-900">
                            £{claimToReview.claimPrice?.toFixed(2) || "0.00"}
                          </p>
                        </div>
                        {claimToReview.approvedBy && (
                          <div className="bg-secondary-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                                Reviewed By
                              </span>
                            </div>
                            <p className="text-sm font-medium text-secondary-900">{claimToReview.approvedBy}</p>
                            {claimToReview.approvedAt && (
                              <p className="text-xs text-secondary-600 mt-1">
                                {DD_MM_YYYY.formatDateAndAppendTime(claimToReview.approvedAt)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Products Section */}
                  <section className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
                      <AlertTriangle className="h-5 w-5 text-primary-600" />
                      <h3 className="text-base font-semibold text-secondary-900">Claim Products</h3>
                    </div>
                    <div className="p-6">
                      {claimToReview.products && claimToReview.products.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-secondary-50">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                  Product ID
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                  Product Name
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                  SKU
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                  Barcode
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                  Unit Price (£)
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                  Claim Quantity
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {claimToReview.products.map((product, index) => (
                                <tr key={index} className="border-b border-secondary-100 hover:bg-secondary-50">
                                  <td className="px-4 py-3 text-sm text-secondary-900 font-mono">
                                    {product.productId}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-secondary-900">{product.productName}</td>
                                  <td className="px-4 py-3 text-sm text-secondary-900 font-mono">{product.sku}</td>
                                  <td className="px-4 py-3 text-sm text-secondary-900 font-mono">
                                    {product.barcode || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-secondary-900">
                                    {product.unitPrice ? `£${product.unitPrice.toFixed(2)}` : "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-secondary-900">{product.claimQuantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-secondary-600">No products listed.</p>
                      )}
                    </div>
                  </section>

                  {/* Claim Details Section */}
                  <section className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
                      <AlertTriangle className="h-5 w-5 text-primary-600" />
                      <h3 className="text-base font-semibold text-secondary-900">Claim Details</h3>
                    </div>
                    <div className="p-6">
                      {claimToReview.claimDetails && claimToReview.claimDetails.length > 0 ? (
                        <div className="space-y-6">
                          {claimToReview.claimDetails.map((detail, index) => (
                            <div key={index} className="border border-secondary-200 rounded-lg p-5 bg-secondary-50">
                              <h4 className="text-sm font-semibold text-secondary-900 mb-4">
                                Claim Detail {index + 1}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide block mb-1">
                                    Claim Type
                                  </span>
                                  <p className="text-sm font-medium text-secondary-900">{detail.claimType}</p>
                                </div>
                                <div>
                                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide block mb-1">
                                    Urgency Level
                                  </span>
                                  <p className="text-sm font-medium text-secondary-900 capitalize">
                                    {detail.urgencyLevel || "-"}
                                  </p>
                                </div>
                              </div>
                              <div>
                                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide block mb-2">
                                  Detailed Description
                                </span>
                                <p className="text-sm text-secondary-900 whitespace-pre-wrap">
                                  {detail.detailedDescription}
                                </p>
                              </div>

                              {/* Non-Order Products Section */}
                              {detail.nonOrderProducts && detail.nonOrderProducts.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-secondary-200">
                                  <h5 className="text-xs font-semibold text-secondary-700 uppercase tracking-wide mb-4">
                                    Non-Order Products
                                  </h5>
                                  <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                      <thead>
                                        <tr className="bg-secondary-100">
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            Product ID
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            Product Name
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            SKU
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            Barcode
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            Unit Price (£)
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            Claim Quantity
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wide border-b border-secondary-200">
                                            Extra Info
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detail.nonOrderProducts.map((product, productIndex) => (
                                          <tr
                                            key={productIndex}
                                            className="border-b border-secondary-100 hover:bg-white"
                                          >
                                            <td className="px-3 py-2 text-sm text-secondary-900 font-mono">
                                              {product.productId}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-secondary-900">
                                              {product.productName}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-secondary-900 font-mono">
                                              {product.sku}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-secondary-900 font-mono">
                                              {product.barcode || "-"}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-secondary-900">
                                              {product.unitPrice ? `£${product.unitPrice.toFixed(2)}` : "-"}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-secondary-900">
                                              {product.claimQuantity}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-secondary-900">
                                              {product.extraInfo || "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-secondary-600">No claim details available.</p>
                      )}
                    </div>
                  </section>

                  {/* Images Section */}
                  {claimToReview.hasImages && (
                    <section className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                      <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
                        <Image className="h-5 w-5 text-primary-600" />
                        <h3 className="text-base font-semibold text-secondary-900">Claim Images</h3>
                      </div>
                      <div className="p-6">
                        {isLoadingImages ? (
                          <p className="text-sm text-secondary-600">Loading images...</p>
                        ) : imageUrls.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {imageUrls.map((imageUrl, index) => (
                              <div key={index} className="relative group">
                                <div className="aspect-square rounded-lg border border-secondary-200 overflow-hidden bg-secondary-50">
                                  <img
                                    src={imageUrl}
                                    alt={`Claim ${index + 1}`}
                                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(imageUrl, "_blank")}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-secondary-600">No images available.</p>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Confirmation Message Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Confirmation Message <span className="text-error-500 ml-1">*</span>
                    </h3>
                    <TextInputBoxLarge
                      id="confirmation-message"
                      placeholder="Enter confirmation message (min. 5 characters)"
                      onChange={(value) => handleInputChange("confirmationMessage", value)}
                      value={formData.confirmationMessage}
                      maxLength={2000}
                      required
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("confirmationMessage")}
                    />
                  </div>

                  {/* Credit File Upload Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      Credit File Upload
                    </h3>
                    <div className="mb-3">
                      <p className="text-sm text-secondary-600 italic">Please upload the credit file(s)</p>
                    </div>
                    <MultiFileUpload
                      uploadText="Upload Credit File(s)"
                      uploadedFiles={formData.creditFiles}
                      onFileChange={(files) => handleInputChange("creditFiles", files)}
                    />
                  </div>
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Action Buttons */}
              <div className="flex items-center gap-4">
                {!claimToReview ? (
                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                    disabled={!formData.claimId}
                    onClick={() => setAction("FETCH")}
                    type="submit"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    <span>Search Claim</span>
                  </button>
                ) : (
                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                    onClick={() => setAction("CONFIRM")}
                    type="submit"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Confirm Claim</span>
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { ReviewClaimConfirmationForm };
